import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Database, ShieldCheck, Award, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { submitPartnership } from "@/lib/api";
import { PARTNERSHIP_NATURES } from "@/lib/constants";

const INITIAL = {
  organisation_name: "",
  contact_person: "",
  designation: "",
  phone: "",
  email: "",
  nature_of_partnership: "",
  message: "",
};

const SCHEMES = [
  {
    name: "One District One Product (ODOP)",
    body: "Solapur is recognised under ODOP for textiles — OVI's registry directly supports the scheme's mapping objectives.",
  },
  {
    name: "PM Vishwakarma Yojana",
    body: "OVI helps identify and onboard eligible handloom weavers and traditional artisans for skill, credit, and toolkit support.",
  },
  {
    name: "Maharashtra Textile Policy",
    body: "OVI aligns with the state textile policy's focus on integrating Solapur's powerloom cluster with formal markets.",
  },
  {
    name: "GI Tag Authentication",
    body: "Authenticate buyers and verify makers for Solapur Chaddar and Solapur Terry Towel — both protected GI products.",
  },
];

export default function Verified() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Verified — OVI";
  }, []);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: typeof e === "string" ? e : e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.organisation_name.trim() || form.organisation_name.trim().length < 2) e.organisation_name = "Please enter your organisation.";
    if (!form.contact_person.trim() || form.contact_person.trim().length < 2) e.contact_person = "Please enter contact person.";
    if (!form.designation.trim() || form.designation.trim().length < 2) e.designation = "Please enter designation.";
    const cleanedPhone = form.phone.replace(/[\s\-()]/g, "");
    if (!/^\+?\d{7,15}$/.test(cleanedPhone)) e.phone = "Enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.nature_of_partnership) e.nature_of_partnership = "Please choose a partnership type.";
    if (!form.message.trim() || form.message.trim().length < 5) e.message = "Please share a short message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitPartnership(form);
      setSuccess(true);
      setForm(INITIAL);
      toast.success("Partnership inquiry sent. We'll be in touch.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--ovi-cream)]">
      {/* Header */}
      <section className="border-b border-[var(--ovi-border)] ovi-texture">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-4">
            OVI Verified · Institutional
          </p>
          <h1
            className="font-serif-display text-3xl sm:text-5xl lg:text-6xl text-[var(--ovi-indigo)] tracking-tight leading-[1.05] max-w-4xl"
            data-testid="verified-page-title"
          >
            India's First Verified Digital Registry of Solapur's Textile Makers.
          </h1>
          <p
            className="mt-6 max-w-3xl text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed"
            data-testid="verified-page-subline"
          >
            OVI Verified is the authenticated database of Solapur's powerloom operators,
            handloom weavers, and textile manufacturers — built for government schemes,
            institutional procurement, and export promotion bodies.
          </p>
        </div>
      </section>

      {/* Section 1: For Government Bodies */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold mb-3">
              01 · For government bodies
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)] leading-[1.1]">
              The full map of the unorganised textile sector.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
              OVI's maker database maps the complete unorganised textile sector of Solapur —
              with verified MSME registration, GST numbers, product categories, and production
              capacity. This data is built to be useful to ministries, district administrations,
              export councils, and policy researchers.
            </p>
            <ul className="mt-6 space-y-3 text-sm sm:text-base text-[var(--ovi-muted-ink)]">
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ovi-terracotta)] flex-shrink-0" />
                Verified MSME / Udyam registration
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ovi-terracotta)] flex-shrink-0" />
                GST number and tax category
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ovi-terracotta)] flex-shrink-0" />
                Product categories and monthly production capacity
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ovi-terracotta)] flex-shrink-0" />
                Workforce size and women-led unit classification
              </li>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              <Tile
                icon={<Building2 className="h-6 w-6" />}
                value="50,000+"
                label="Powerloom units in Solapur"
              />
              <Tile
                icon={<Database className="h-6 w-6" />}
                value="2 Lakh+"
                label="Textile workers in the cluster"
              />
              <Tile
                icon={<Award className="h-6 w-6" />}
                value="2 GI Tags"
                label="Solapur Chaddar & Solapur Terry Towel"
              />
              <Tile
                icon={<ShieldCheck className="h-6 w-6" />}
                value="India's First"
                label="Digital maker registry of Solapur"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Scheme Integration */}
      <section className="bg-[var(--ovi-cream-2)] border-y border-[var(--ovi-border)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold mb-3">
            02 · Scheme integration
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)] leading-[1.1] max-w-3xl">
            Aligned with India's textile and craft schemes.
          </h2>
          <p className="mt-5 max-w-3xl text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
            OVI is designed to feed verified data and provenance to the schemes that already exist for Indian textile makers.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {SCHEMES.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.2, 0.7, 0.2, 1] }}
                className="bg-[var(--ovi-cream)] border border-[var(--ovi-border)] rounded-2xl p-6 sm:p-7"
                data-testid={`verified-scheme-${i + 1}`}
              >
                <p className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[var(--ovi-terracotta)]">
                  Scheme 0{i + 1}
                </p>
                <h3 className="mt-3 font-serif-display text-xl sm:text-2xl text-[var(--ovi-indigo)]">
                  {s.name}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-[var(--ovi-muted-ink)] leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Partnership Form */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold mb-3">
            03 · Partner with OVI
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">
            Let's build the registry, together.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[var(--ovi-muted-ink)]">
            We work with ministries, councils, NGOs and research bodies. Drop us a note.
          </p>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-8 sm:p-10 text-center"
            data-testid="verified-form-success"
          >
            <CheckCircle2 className="h-10 w-10 text-[var(--ovi-terracotta)] mx-auto" />
            <h3 className="mt-4 font-serif-display text-2xl text-[var(--ovi-indigo)]">
              Thank you.
            </h3>
            <p className="mt-3 text-base sm:text-lg text-[var(--ovi-ink)] max-w-lg mx-auto leading-relaxed">
              Our team will reach out within 2 working days. You are helping build something important for Solapur.
            </p>
            <Button
              onClick={() => setSuccess(false)}
              variant="outline"
              className="mt-6 border-[var(--ovi-indigo)] text-[var(--ovi-indigo)] rounded-full h-11 px-6"
              data-testid="verified-form-new"
            >
              Submit another inquiry
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={onSubmit} noValidate data-testid="verified-partnership-form"
            className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field id="organisation_name" label="Organisation name" error={errors.organisation_name} span="md:col-span-2">
              <Input id="organisation_name" value={form.organisation_name} onChange={update("organisation_name")}
                placeholder="Ministry / Council / NGO / Institute" className="verified-input"
                data-testid="verified-form-organisation-input" />
            </Field>
            <Field id="contact_person" label="Contact person" error={errors.contact_person}>
              <Input id="contact_person" value={form.contact_person} onChange={update("contact_person")}
                placeholder="Full name" className="verified-input"
                data-testid="verified-form-contact-input" autoComplete="name" />
            </Field>
            <Field id="designation" label="Designation" error={errors.designation}>
              <Input id="designation" value={form.designation} onChange={update("designation")}
                placeholder="Director, Officer, Lead, etc." className="verified-input"
                data-testid="verified-form-designation-input" />
            </Field>
            <Field id="phone" label="Phone" error={errors.phone}>
              <Input id="phone" value={form.phone} onChange={update("phone")}
                placeholder="+91 00000 00000" inputMode="tel" className="verified-input"
                data-testid="verified-form-phone-input" autoComplete="tel" />
            </Field>
            <Field id="email" label="Email" error={errors.email}>
              <Input id="email" type="email" value={form.email} onChange={update("email")}
                placeholder="you@organisation.gov.in" className="verified-input"
                data-testid="verified-form-email-input" autoComplete="email" />
            </Field>
            <Field id="nature_of_partnership" label="Nature of partnership" error={errors.nature_of_partnership} span="md:col-span-2">
              <Select value={form.nature_of_partnership} onValueChange={update("nature_of_partnership")}>
                <SelectTrigger id="nature_of_partnership" className="verified-input h-11"
                  data-testid="verified-form-nature-select">
                  <SelectValue placeholder="Choose a partnership type" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--ovi-cream-2)] border-[var(--ovi-border)]">
                  {PARTNERSHIP_NATURES.map((c) => (
                    <SelectItem key={c} value={c} className="focus:bg-[var(--ovi-cream)]"
                      data-testid={`verified-form-nature-option-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="message" label="Message" error={errors.message} span="md:col-span-2">
              <Textarea id="message" value={form.message} onChange={update("message")} rows={5}
                placeholder="Tell us about your objective, scheme, region of focus, timelines, etc."
                className="verified-input min-h-[130px]"
                data-testid="verified-form-message-input" />
            </Field>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-[var(--ovi-muted-ink)] max-w-sm">
                Your information is kept private and used only for partnership discussions.
              </p>
              <Button type="submit" disabled={submitting}
                className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-7 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-70"
                data-testid="verified-form-submit-button">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</>) : "Send partnership inquiry"}
              </Button>
            </div>
          </form>
        )}
      </section>

      <style>{`
        .verified-input {
          background-color: var(--ovi-cream);
          border-color: var(--ovi-border);
        }
        .verified-input:focus-visible {
          --tw-ring-color: var(--ovi-terracotta);
        }
      `}</style>
    </div>
  );
}

const Field = ({ id, label, error, children, span = "" }) => (
  <div className={`flex flex-col gap-1.5 ${span}`}>
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] font-medium text-[var(--ovi-muted-ink)]">
      {label}
    </Label>
    {children}
    {error ? (
      <p className="text-xs text-[var(--ovi-terracotta)] mt-0.5" data-testid={`verified-form-${id}-error`}>
        {error}
      </p>
    ) : null}
  </div>
);

const Tile = ({ icon, value, label }) => (
  <div className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-5">
    <div className="h-9 w-9 rounded-full bg-[var(--ovi-indigo)]/8 text-[var(--ovi-indigo)] flex items-center justify-center">
      {icon}
    </div>
    <p className="mt-3 font-serif-display text-2xl sm:text-3xl text-[var(--ovi-indigo)]">{value}</p>
    <p className="mt-1 text-xs sm:text-sm text-[var(--ovi-muted-ink)] leading-tight">{label}</p>
  </div>
);
